import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import {
  useGetAllQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  usePublishAnnouncementMutation,
  useUpdateAnnouncementReadStatusMutation,
  useDeleteAnnouncementMutation
} from '../../../../api';
import { toastService } from '../../../../utils/toastService';
import { convertFiltersToPrimeReactFormat, createInitialFilters } from '../../../../utils/filterUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';


const useAnnouncementData = () => {
  const { getCurrentLanguageId, th } = useLanguage();
  const languageId = typeof getCurrentLanguageId === 'function'
    ? getCurrentLanguageId()
    : getCurrentLanguageId;

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [pagination, setPagination] = useState({ PageNumber: 1, PageSize: 10 });
  const [globalFilterValue, setGlobalFilterValue] = useState('');

  const filterTypeMap = useMemo(
    () => ({
      announcementType: { type: 'select', matchMode: FilterMatchMode.EQUALS },
      isActive: { type: 'boolean', matchMode: FilterMatchMode.EQUALS }
    }),
    []
  );
  const filterFieldNames = useMemo(() => ['announcementType', 'isActive'], []);

  const initialFilters = useMemo(() => {
    const base = createInitialFilters(filterFieldNames, filterTypeMap);

    // isActive varsayılan true olsun
    base.isActive = { value: true, matchMode: FilterMatchMode.EQUALS };

    return base;
  }, [filterFieldNames, filterTypeMap]);

  const [filters, setFilters] = useState(initialFilters);

  const useDebounce = (callback, delay) => {
    const timeoutRef = useRef(null);
    return useCallback(
      (...args) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          callback(...args);
          timeoutRef.current = null;
        }, delay);
      },
      [callback, delay]
    );
  };

  const activeStatusValue = useMemo(() => {
    return filters.isActive?.value === undefined
      ? undefined
      : filters.isActive?.value
        ? 1
        : 0;
  }, [filters.isActive?.value]);

  const announcementTypeValue = useMemo(() => {
    const f = filters.announcementType;
    if (!f) return undefined;

    if ('value' in f && f.value !== undefined && f.value !== null && f.value !== '')
      return f.value;

    const c = f.constraints?.[0]?.value;
    return (c === '' || c === null || c === undefined) ? undefined : c;
  }, [filters.announcementType]);

  const queryParams = useMemo(() => {
    const params = { PageNumber: pagination.PageNumber, PageSize: pagination.PageSize, LanguageId: languageId };
    if (activeStatusValue !== undefined) params.IsActive = activeStatusValue;
    if (announcementTypeValue !== undefined) params.AnnouncementType = announcementTypeValue;
    return params;
  }, [pagination.PageNumber, pagination.PageSize, activeStatusValue, announcementTypeValue, languageId]);

  const {
    data: announcement = [],
    isLoading: isLoadingAnnouncements,
    isFetching,
    error: announcementError,
    refetch
  } = useGetAllQuery(queryParams);

  const [createAnnouncement, { isLoading: isCreating }] = useCreateAnnouncementMutation();
  const [updateAnnouncement, { isLoading: isUpdating }] = useUpdateAnnouncementMutation();
  const [publishAnnouncement, { isLoading: isPublishing }] = usePublishAnnouncementMutation();
  const [updateAnnouncementRead, { isLoading: isUpdatingRead }] = useUpdateAnnouncementReadStatusMutation();
  const [deleteAnnouncement, { isLoading: isDeleting }] = useDeleteAnnouncementMutation();

  useEffect(() => {
    if (!announcement) {
      setAnnouncements([]);
      setTotalCount(0);
      return;
    }
    if (Array.isArray(announcement.items)) {
      setAnnouncements(announcement.items);
      setTotalCount(announcement.totalCount ?? announcement.items.length);
    } else if (Array.isArray(announcement)) {
      setAnnouncements(announcement);
      setTotalCount(announcement.length);
    } else if (Array.isArray(announcement.data)) {
      setAnnouncements(announcement.data);
      setTotalCount(announcement.data.length);
    } else {
      setAnnouncements([]);
      setTotalCount(0);
    }
  }, [announcement]);

  useEffect(() => {
    setLoading(isLoadingAnnouncements || isFetching);
  }, [isLoadingAnnouncements, isFetching]);

  // error log
  useEffect(() => {
    if (announcementError) {
      console.error('Error fetching announcements:', announcementError);
    }
  }, [announcementError]);

  const updateFilters = useCallback((key, value) => {
    setFilters(prev => {
      if (key === 'global') {
        const nextGlobal = { ...prev.global, value };
        if (JSON.stringify(nextGlobal) === JSON.stringify(prev.global)) return prev;
        return { ...prev, global: nextGlobal };
      } else {
        const current = prev[key];
        if (!current) return prev;

        if (key === 'isActive') {
          const next = { value, matchMode: FilterMatchMode.EQUALS };
          if (JSON.stringify(next) === JSON.stringify(current)) return prev;
          return { ...prev, [key]: next };
        } else {
          const next = {
            operator: FilterOperator.AND,
            constraints: [{ value, matchMode: FilterMatchMode.CONTAINS }]
          };
          if (JSON.stringify(next) === JSON.stringify(current)) return prev;
          return { ...prev, [key]: next };
        }
      }
    });
  }, []);

  const debouncedUpdateFilters = useDebounce(updateFilters, 300);

  // global search
  const onGlobalFilterChange = useCallback(
    e => {
      const value = e.target.value;
      setGlobalFilterValue(value);
      debouncedUpdateFilters('global', value);
    },
    [debouncedUpdateFilters]
  );

  // delete
  const handleDelete = async (rowOrId) => {
    const id = Number(rowOrId?.id ?? rowOrId?.Id ?? rowOrId);
    if (!Number.isFinite(id)) {
      toastService.error('Geçersiz kayıt (ID yok).');
      return false;
    }

    try {
      const res = await deleteAnnouncement(id).unwrap();
      const ok =
        res === undefined || res === null ||
        res?.success === true ||
        res?.isSuccess === true ||
        res?.Success === true;

      if (!ok) {
        const msg = res?.message || res?.error || th('announcement.deleteErrorMessage');
        throw new Error(msg);
      }

      toastService.success(th('messages.deleteSuccess'));
      refetch();
      return true;
    } catch (error) {
      console.error('Delete error:', error);
      toastService.error(th('messages.deleteError'));
      return false;
    }
  };

  // create/update
  const saveAnnouncement = async (announcementData, isEdit, currentAnnouncement) => {
    try {
      if (isEdit && currentAnnouncement) {
        await updateAnnouncement(announcementData).unwrap();
        toastService.success(th('announcement.updateSuccessMessage'));
      } else {
        await createAnnouncement(announcementData).unwrap();
        toastService.success(th('announcement.successMessageDetail'));
      }
      refetch();
      return true;
    } catch (error) {
      return false;
    }
  };

  const filterFields = useMemo(
    () => [
      {
        field: 'announcementType',
        headerName: th('announcement.placeholders.type'),
        type: 'select',
         filterPlaceholder: th('announcement.placeholders.type'),
        options: [
          { label: th('announcement.typeLabels.sales'), value: 128 },
          { label: th('announcement.typeLabels.marketing'), value: 129 },
          { label: th('announcement.typeLabels.afterSales'), value: 130 },
          { label: th('announcement.typeLabels.warranty'), value: 131 },
          { label: th('announcement.typeLabels.spareParts'), value: 132 }
        ]
      },
      {
        field: 'isActive',
        headerName: th('common.active'),
        type: 'boolean',
        defaultValue: true
      }
    ],
    [th]
  );

  const customFilterTypes = useMemo(
    () => ({
      announcementType: 'select',
      isActive: 'boolean'
    }),
    []
  );

  const customFilterOptions = useMemo(
    () => ({
      isActive: [
        { label: th('common.active'), value: true },
        { label: th('common.inactive'), value: false }
      ]
    }),
    [th]
  );

  // free-text search (external)
  const handleSearch = useCallback(
    searchValue => {
      setGlobalFilterValue(searchValue);
      debouncedUpdateFilters('global', searchValue);
    },
    [debouncedUpdateFilters]
  );

  const handleFilterChange = useCallback((updatedFilters) => {
    const converted = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
    setFilters(converted);
    setPagination(prev => ({ ...prev, PageNumber: 1 }));
  }, [filters, filterTypeMap]);

  const resetGlobalFilter = useCallback(() => {
    setGlobalFilterValue('');
    setFilters(prev => ({
      ...prev,
      global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    }));
  }, []);

  const handleResetFilters = useCallback(() => {
    const reset = createInitialFilters(filterFieldNames, filterTypeMap);
    reset.isActive = { value: true, matchMode: FilterMatchMode.EQUALS };

    // ensure shapes
    Object.keys(reset).forEach(key => {
      if (key !== 'global' && key !== 'isActive') {
        const def = {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        };
        reset[key] = def;
      }
    });

    setFilters(reset);
    resetGlobalFilter();

    setPagination(prev => ({
      PageNumber: 1,
      PageSize: prev.PageSize
    }));

    setTimeout(() => refetch(), 100);
  }, [filterFieldNames, filterTypeMap, resetGlobalFilter, refetch]);

  const handleApplyFilters = useCallback((updatedFilters) => {
    const converted = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
    setFilters(converted);
    setPagination(prev => ({ ...prev, PageNumber: 1 }));
  }, [filters, filterTypeMap]);

  // pagination
  const onPageChange = useCallback(event => {
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    setPagination(prev => ({
      ...prev,
      PageNumber: newPageNumber,
      PageSize: event.rows
    }));
  }, []);

  return {
    // data
    announcements: announcements,
    loading,
    totalCount,
    filters,
    globalFilterValue,
    pagination,

    // mutation states
    isCreating,
    isUpdating,
    isDeleting,
    isPublishing,
    isUpdatingRead,

    // filter meta
    filterFields,
    customFilterTypes,
    customFilterOptions,

    // actions
    handleDelete,
    saveAnnouncement,
    publishAnnouncement,
    updateAnnouncementRead,
    handleSearch,
    handleFilterChange,
    handleResetFilters,
    handleApplyFilters,
    onGlobalFilterChange,
    onPageChange,

    // control
    refetch
  };
};

export default useAnnouncementData;